<script setup lang="ts">
  /**
   * The entire "Default Payment Terms" setting, end-to-end:
   * card row + edit modal + form + submit. One file = one setting.
   */

  import { computed, ref, watch } from 'vue'
  import { IconEdit } from '@gat-ship/icons'
  import {
    Button,
    Checkbox,
    FormField,
    FormSubmit,
    InputGroupNumber,
    Modal,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalHeaderIcon,
    ModalHeaderTitle,
    ModalSection,
    useForm,
    z
  } from '@gat-ship/vue-components'
  import { FieldGroup } from '@gat-ship/vue-components'
  import type { ScopedSetting, TypedSettingValues } from '@gat-ship/types'
  import SettingCard from './SettingCard.vue'
  import SettingOverrideCard from './SettingOverrideCard.vue'
  import { useAgencySettings } from '../composables/useAgencySettings'
  import { useSettingDefinitions } from '../composables/useSettingDefinitions'
  import { useSettingsHelpers } from '../composables/useSettingsHelpers'
  import { useAgencyStructureContext } from '../composables/useAgencyStructureContext'

  const SETTING_NAME = 'client.billing.defaultPaymentTerms'
  type Value = TypedSettingValues[typeof SETTING_NAME]
  const TITLE = 'Default Payment Terms'
  const DESCRIPTION =
    'Sets the default payment terms used when creating invoices for this agency.'

  const agencySettings = useAgencySettings()
  const settingDefinitions = useSettingDefinitions()
  const agencyStructure = useAgencyStructureContext()
  const {
    getScopedSettingValue,
    getInheritLabel,
    resolveInheritedFrom,
    canRevertToInherit
  } = useSettingsHelpers()

  watch(
    agencySettings.agencyId,
    (id) => {
      if (id) void agencySettings.refetch([SETTING_NAME])
    },
    { immediate: true }
  )

  const scopedSetting = computed(
    () =>
      agencySettings.scopedSettings[SETTING_NAME] as
        | ScopedSetting<Value>
        | undefined
  )
  const definition = computed(() => settingDefinitions.get(SETTING_NAME))

  // --- Card display state ---
  const isLoading = computed(
    () => agencySettings.isLoading.value && !scopedSetting.value
  )

  function formatValue(value: Value | null | undefined): string {
    if (!value) return 'Not set'
    if (value.useEndOfMonthTerms) {
      return `End of month + ${value.paymentPeriodInDays} days`
    }
    return `${value.paymentPeriodInDays} days`
  }

  const valueText = computed(() => {
    if (isLoading.value) return 'Pending...'
    return formatValue(getScopedSettingValue(scopedSetting.value))
  })

  const statusText = computed(() => {
    if (isLoading.value) return 'Loading...'
    const s = scopedSetting.value
    if (!s) return 'Not found'
    if (s.layerStatus === 'forbidden') return 'Forbidden'
    if (s.layerStatus === 'assigned') return 'Assigned'
    const firstParent = s.parentChain[0]
    if (!firstParent) {
      return definition.value?.hasDefaultValue ? 'Gatship Default' : 'Not set'
    }
    return firstParent.layerScope === 'default'
      ? 'Gatship Default'
      : 'Inherited'
  })

  // --- Modal ---
  const isOpen = ref(false)

  const schema = z
    .object({
      mode: z.enum(['inherit', 'custom']),
      paymentPeriodInDays: z.number().int().positive().optional(),
      useEndOfMonthTerms: z.boolean()
    })
    .superRefine((data, ctx) => {
      if (
        data.mode === 'custom' &&
        (data.paymentPeriodInDays === undefined ||
          data.paymentPeriodInDays === null)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Payment period is required',
          path: ['paymentPeriodInDays']
        })
      }
    })
  type FormValues = z.infer<typeof schema>

  const form = useForm({
    defaultValues: {
      mode: 'inherit',
      paymentPeriodInDays: undefined,
      useEndOfMonthTerms: false
    } as FormValues,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      if (value.mode === 'inherit') {
        await agencySettings.remove(SETTING_NAME)
      } else if (
        value.mode === 'custom' &&
        value.paymentPeriodInDays !== undefined
      ) {
        await agencySettings.upsert(SETTING_NAME, {
          paymentPeriodInDays: value.paymentPeriodInDays,
          useEndOfMonthTerms: value.useEndOfMonthTerms
        })
      }
      isOpen.value = false
    }
  })

  // --- Inherit card details ---
  const inherited = computed(() =>
    resolveInheritedFrom(
      scopedSetting.value,
      agencyStructure.agencyStructure.value,
      agencySettings.agencyId.value
    )
  )
  const inheritLabel = computed(() => getInheritLabel(scopedSetting.value))
  const canInherit = computed(() =>
    canRevertToInherit(
      definition.value,
      agencyStructure.agencyStructure.value,
      agencySettings.agencyId.value
    )
  )
  const inheritedValueText = computed(() => {
    const v = inherited.value?.value
    if (!v || typeof v === 'string')
      return typeof v === 'string' ? v : undefined
    return formatValue(v as Value)
  })

  function openModal() {
    const current = scopedSetting.value
    if (current?.layerStatus === 'assigned') {
      form.reset({
        mode: 'custom',
        paymentPeriodInDays: current.value.paymentPeriodInDays,
        useEndOfMonthTerms: current.value.useEndOfMonthTerms
      })
    } else {
      form.reset({
        mode: canInherit.value ? 'inherit' : 'custom',
        paymentPeriodInDays: undefined,
        useEndOfMonthTerms: false
      })
    }
    isOpen.value = true
  }
</script>

<template>
  <SettingCard
    :title="TITLE"
    :description="DESCRIPTION"
    :value-text="valueText"
    :status-text="statusText"
    :is-loading="isLoading"
    @click="openModal"
  />
  <modal v-model:open="isOpen">
    <ModalHeader>
      <ModalHeaderTitle>
        <ModalHeaderIcon>
          <IconEdit class="size-5" :stroke-width="1.5" />
        </ModalHeaderIcon>
        {{ TITLE }}
      </ModalHeaderTitle>
    </ModalHeader>
    <ModalContent>
      <ModalSection>
        <SettingOverrideCard
          :form="form"
          name="mode"
          :inherit-label="inheritLabel"
          :can-revert-to-inherit="canInherit"
          :inherited-from-text="inherited?.inheritedFrom"
          :inherited-value-text="inheritedValueText"
        >
          <template #custom>
            <FieldGroup>
              <FormField
                :form="form"
                name="paymentPeriodInDays"
                label="Payment Period (Days)"
                required
              >
                <template #default="{ field }">
                  <InputGroupNumber
                    :id="field.name"
                    :model-value="field.state.value"
                    placeholder="e.g. 30"
                    @update:model-value="field.handleChange"
                    @blur="field.handleBlur"
                  />
                </template>
              </FormField>
              <component :is="form.Field" name="useEndOfMonthTerms">
                <template #default="{ field }">
                  <label
                    class="group/field flex cursor-pointer items-start gap-2 px-3 py-2"
                  >
                    <Checkbox
                      :model-value="!!field.state.value"
                      @update:model-value="
                        (v) => {
                          field.handleChange(v === true)
                          field.handleBlur()
                        }
                      "
                    />
                    <span class="flex flex-col gap-1">
                      <span
                        class="text-xs font-medium leading-4 text-neutral-700 dark:text-neutral-200"
                      >
                        Use End of Month Terms
                      </span>
                      <span
                        class="text-muted-foreground whitespace-pre-line text-xs leading-snug"
                      >
                        Counts days from the last day of the current month
                        instead of the invoice date.
                      </span>
                    </span>
                  </label>
                </template>
              </component>
            </FieldGroup>
          </template>
        </SettingOverrideCard>
      </ModalSection>
    </ModalContent>
    <ModalFooter>
      <FormSubmit :form="form" label="Save" :require-dirty="false" />
      <Button variant="ghost" @click="isOpen = false"> Cancel </Button>
    </ModalFooter>
  </modal>
</template>
